import React, { useState } from 'react';
import {
  XMarkIcon,
  TrashIcon,
  ExclamationTriangleIcon,
  DocumentArrowDownIcon,
  CheckCircleIcon,
  ClockIcon,
  ShieldCheckIcon,
  InformationCircleIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';
import type { Tenant } from './TenantTable';

interface DeletionReason {
  id: string;
  label: string;
  description: string;
  requiresApproval: boolean;
}

interface DataExportOptions {
  includeUsers: boolean;
  includeAnalytics: boolean;
  includeBilling: boolean;
  includeContent: boolean;
  format: 'json' | 'csv' | 'xml';
  encryptExport: boolean;
}

interface DeletionRequest {
  tenantId: string;
  reason: string;
  reasonDescription: string;
  dataExport: DataExportOptions;
  notifyUsers: boolean;
  gracePeriod: number; // days
  confirmationText: string;
  scheduleFor?: Date;
}

interface TenantDeletionWorkflowProps {
  isOpen: boolean;
  tenant: Tenant | null;
  onClose: () => void;
  onConfirmDeletion: (request: DeletionRequest) => void;
}

const TenantDeletionWorkflow: React.FC<TenantDeletionWorkflowProps> = ({
  isOpen,
  tenant,
  onClose,
  onConfirmDeletion
}) => {
  const [currentStep, setCurrentStep] = useState(1);
  const [deletionRequest, setDeletionRequest] = useState<Partial<DeletionRequest>>({
    reason: '',
    reasonDescription: '',
    dataExport: {
      includeUsers: true,
      includeAnalytics: false,
      includeBilling: true,
      includeContent: false,
      format: 'json',
      encryptExport: true
    },
    notifyUsers: true,
    gracePeriod: 7,
    confirmationText: ''
  });
  const [isProcessing, setIsProcessing] = useState(false);
  const [showFinalConfirmation, setShowFinalConfirmation] = useState(false);

  const steps = [
    { id: 1, name: 'Reason & Review', icon: InformationCircleIcon },
    { id: 2, name: 'Data Export', icon: DocumentArrowDownIcon },
    { id: 3, name: 'Settings', icon: Cog6ToothIcon },
    { id: 4, name: 'Confirmation', icon: ShieldCheckIcon }
  ];

  const deletionReasons: DeletionReason[] = [
    {
      id: 'client_request',
      label: 'Client Request',
      description: 'Client has requested account deletion',
      requiresApproval: false
    },
    {
      id: 'non_payment',
      label: 'Non-Payment',
      description: 'Account suspended due to non-payment',
      requiresApproval: false
    },
    {
      id: 'policy_violation',
      label: 'Policy Violation',
      description: 'Terms of service or policy violation',
      requiresApproval: true
    },
    {
      id: 'data_breach',
      label: 'Security Incident',
      description: 'Security breach or compromise',
      requiresApproval: true
    },
    {
      id: 'merger_acquisition',
      label: 'Merger/Acquisition',
      description: 'Account being merged with another tenant',
      requiresApproval: false
    },
    {
      id: 'business_closure',
      label: 'Business Closure',
      description: 'Client business has ceased operations',
      requiresApproval: false
    },
    {
      id: 'data_migration',
      label: 'Data Migration',
      description: 'Data being migrated to different platform',
      requiresApproval: false
    },
    {
      id: 'other',
      label: 'Other',
      description: 'Other reason (please specify)',
      requiresApproval: true
    }
  ];

  const updateDeletionRequest = (updates: Partial<DeletionRequest>) => {
    setDeletionRequest(prev => ({ ...prev, ...updates }));
  };

  const updateDataExport = (updates: Partial<DataExportOptions>) => {
    setDeletionRequest(prev => ({
      ...prev,
      dataExport: { ...prev.dataExport!, ...updates }
    }));
  };

  const validateStep = (step: number): boolean => {
    switch (step) {
      case 1:
        return !!(deletionRequest.reason && deletionRequest.reasonDescription);
      case 2:
        return true; // Data export options are optional
      case 3:
        return deletionRequest.gracePeriod !== undefined && deletionRequest.gracePeriod >= 0;
      case 4:
        return deletionRequest.confirmationText === `DELETE ${tenant?.name}`;
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (validateStep(currentStep)) {
      setCurrentStep(prev => Math.min(prev + 1, 4));
    } else {
      toast.error('Please complete all required fields');
    }
  };

  const handlePrevious = () => {
    setCurrentStep(prev => Math.max(prev - 1, 1));
  };

  const handleSubmit = async () => {
    if (!tenant || !validateStep(4)) return;

    const completeRequest: DeletionRequest = {
      tenantId: tenant.id,
      reason: deletionRequest.reason!,
      reasonDescription: deletionRequest.reasonDescription!,
      dataExport: deletionRequest.dataExport!,
      notifyUsers: deletionRequest.notifyUsers!,
      gracePeriod: deletionRequest.gracePeriod!,
      confirmationText: deletionRequest.confirmationText!,
      scheduleFor: deletionRequest.scheduleFor
    };

    setIsProcessing(true);
    try {
      await onConfirmDeletion(completeRequest);
      toast.success('Tenant deletion initiated successfully');
      onClose();
    } catch (error) {
      toast.error('Failed to initiate tenant deletion');
    } finally {
      setIsProcessing(false);
      setShowFinalConfirmation(false);
    }
  };

  const getStepValidation = (step: number) => {
    return validateStep(step) ? 'text-green-600' : 'text-gray-400';
  };

  if (!isOpen || !tenant) return null;

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <div className="space-y-6">
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-start space-x-3">
                <ExclamationTriangleIcon className="h-6 w-6 text-red-500 mt-0.5 flex-shrink-0" />
                <div>
                  <h4 className="font-medium text-red-900">Permanent Action Warning</h4>
                  <p className="text-sm text-red-700 mt-1">
                    This action will permanently delete the tenant account and all associated data. 
                    This action cannot be undone once the grace period expires.
                  </p>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">Tenant Information</h4>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Tenant Name</p>
                    <p className="text-sm text-gray-900">{tenant.name}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Domain</p>
                    <p className="text-sm text-gray-900">{tenant.domain}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Users</p>
                    <p className="text-sm text-gray-900">{tenant.users_count}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Status</p>
                    <p className="text-sm text-gray-900 capitalize">{tenant.subscription_status}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Created</p>
                    <p className="text-sm text-gray-900">{new Date(tenant.created_at).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-700">Last Activity</p>
                    <p className="text-sm text-gray-900">
                      {tenant.last_activity ? new Date(tenant.last_activity).toLocaleDateString() : 'Never'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                Reason for Deletion *
              </label>
              <div className="space-y-2">
                {deletionReasons.map((reason) => (
                  <label key={reason.id} className="flex items-start space-x-3">
                    <input
                      type="radio"
                      name="deletionReason"
                      value={reason.id}
                      checked={deletionRequest.reason === reason.id}
                      onChange={(e) => updateDeletionRequest({ reason: e.target.value })}
                      className="mt-1 rounded border-gray-300 text-red-600 shadow-sm focus:border-red-300 focus:ring focus:ring-red-200 focus:ring-opacity-50"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900 flex items-center">
                        {reason.label}
                        {reason.requiresApproval && (
                          <span className="ml-2 px-2 py-0.5 text-xs bg-yellow-100 text-yellow-800 rounded-full">
                            Requires Approval
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600">{reason.description}</div>
                    </div>
                  </label>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Detailed Description *
              </label>
              <textarea
                rows={4}
                value={deletionRequest.reasonDescription}
                onChange={(e) => updateDeletionRequest({ reasonDescription: e.target.value })}
                className="w-full rounded-md border-gray-300 shadow-sm focus:border-red-300 focus:ring focus:ring-red-200 focus:ring-opacity-50"
                placeholder="Please provide detailed information about why this tenant is being deleted..."
              />
              <p className="text-xs text-gray-500 mt-1">
                This information will be logged for audit purposes and may be required for compliance.
              </p>
            </div>
          </div>
        );

      case 2:
        return (
          <div className="space-y-6">
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">Data Export Configuration</h4>
              <p className="text-sm text-gray-600 mb-4">
                Configure what data to export before deletion. Exports are encrypted and will be available for 30 days.
              </p>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Export Format
                  </label>
                  <select
                    value={deletionRequest.dataExport?.format}
                    onChange={(e) => updateDataExport({ format: e.target.value as 'json' | 'csv' | 'xml' })}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  >
                    <option value="json">JSON (Recommended)</option>
                    <option value="csv">CSV (Tabular Data)</option>
                    <option value="xml">XML (Structured Data)</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">
                    Data to Include
                  </label>
                  <div className="space-y-3">
                    {[
                      { key: 'includeUsers', label: 'User Accounts & Profiles', description: 'User information, roles, and preferences' },
                      { key: 'includeAnalytics', label: 'Analytics Data', description: 'Historical usage and performance data' },
                      { key: 'includeBilling', label: 'Billing Information', description: 'Invoices, payments, and transaction history' },
                      { key: 'includeContent', label: 'Content & Files', description: 'Uploaded files and generated content' }
                    ].map((option) => (
                      <label key={option.key} className="flex items-start space-x-3">
                        <input
                          type="checkbox"
                          checked={deletionRequest.dataExport?.[option.key as keyof DataExportOptions] as boolean}
                          onChange={(e) => updateDataExport({ [option.key]: e.target.checked })}
                          className="mt-1 rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                        />
                        <div>
                          <div className="text-sm font-medium text-gray-900">{option.label}</div>
                          <div className="text-sm text-gray-600">{option.description}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      checked={deletionRequest.dataExport?.encryptExport}
                      onChange={(e) => updateDataExport({ encryptExport: e.target.checked })}
                      className="mt-1 rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900">Encrypt Export</div>
                      <div className="text-sm text-gray-600">
                        Encrypt the exported data with AES-256 encryption (Recommended)
                      </div>
                    </div>
                  </label>
                </div>

                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                  <div className="flex items-start space-x-3">
                    <InformationCircleIcon className="h-5 w-5 text-blue-500 mt-0.5 flex-shrink-0" />
                    <div className="text-sm text-blue-700">
                      <p className="font-medium mb-1">Export Information</p>
                      <ul className="space-y-1">
                        <li>• Exports will be generated before deletion begins</li>
                        <li>• Download links will be sent to administrative contacts</li>
                        <li>• Exports are automatically deleted after 30 days</li>
                        <li>• Large exports may take several hours to complete</li>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 3:
        return (
          <div className="space-y-6">
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">Deletion Settings</h4>

              <div className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Grace Period (Days)
                  </label>
                  <select
                    value={deletionRequest.gracePeriod}
                    onChange={(e) => updateDeletionRequest({ gracePeriod: parseInt(e.target.value) })}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  >
                    <option value={0}>Immediate (No grace period)</option>
                    <option value={1}>1 Day</option>
                    <option value={3}>3 Days</option>
                    <option value={7}>7 Days (Recommended)</option>
                    <option value={14}>14 Days</option>
                    <option value={30}>30 Days</option>
                  </select>
                  <p className="text-xs text-gray-500 mt-1">
                    Period during which deletion can be cancelled and account can be recovered
                  </p>
                </div>

                <div>
                  <label className="flex items-start space-x-3">
                    <input
                      type="checkbox"
                      checked={deletionRequest.notifyUsers}
                      onChange={(e) => updateDeletionRequest({ notifyUsers: e.target.checked })}
                      className="mt-1 rounded border-gray-300 text-blue-600 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                    />
                    <div>
                      <div className="text-sm font-medium text-gray-900">Notify Users</div>
                      <div className="text-sm text-gray-600">
                        Send email notifications to all tenant users about the scheduled deletion
                      </div>
                    </div>
                  </label>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Schedule Deletion (Optional)
                  </label>
                  <input
                    type="datetime-local"
                    min={new Date().toISOString().slice(0, 16)}
                    onChange={(e) => updateDeletionRequest({ 
                      scheduleFor: e.target.value ? new Date(e.target.value) : undefined 
                    })}
                    className="w-full rounded-md border-gray-300 shadow-sm focus:border-blue-300 focus:ring focus:ring-blue-200 focus:ring-opacity-50"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Leave empty to start deletion process immediately after confirmation
                  </p>
                </div>
              </div>

              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <ClockIcon className="h-5 w-5 text-yellow-500 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-yellow-700">
                    <p className="font-medium mb-1">Deletion Timeline</p>
                    <ol className="list-decimal list-inside space-y-1">
                      <li>Grace period begins immediately after confirmation</li>
                      <li>Users are notified (if enabled) and access is suspended</li>
                      <li>Data export is generated and made available for download</li>
                      <li>After grace period expires, permanent deletion begins</li>
                      <li>All data and account information is permanently removed</li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );

      case 4:
        return (
          <div className="space-y-6">
            <div>
              <h4 className="text-lg font-medium text-gray-900 mb-4">Final Confirmation</h4>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 space-y-4">
                <div>
                  <h5 className="font-medium text-gray-900 mb-2">Deletion Summary</h5>
                  <div className="space-y-2 text-sm text-gray-700">
                    <p><span className="font-medium">Tenant:</span> {tenant.name}</p>
                    <p><span className="font-medium">Reason:</span> {deletionReasons.find(r => r.id === deletionRequest.reason)?.label}</p>
                    <p><span className="font-medium">Grace Period:</span> {deletionRequest.gracePeriod} days</p>
                    {deletionRequest.scheduleFor && (
                      <p><span className="font-medium">Scheduled For:</span> {deletionRequest.scheduleFor.toLocaleString()}</p>
                    )}
                    <p><span className="font-medium">Notify Users:</span> {deletionRequest.notifyUsers ? 'Yes' : 'No'}</p>
                  </div>
                </div>

                <div>
                  <h5 className="font-medium text-gray-900 mb-2">Data Export</h5>
                  <div className="space-y-1 text-sm text-gray-700">
                    <p>Format: {deletionRequest.dataExport?.format?.toUpperCase()}</p>
                    <p>Includes: {Object.entries(deletionRequest.dataExport || {})
                      .filter(([key, value]) => key.startsWith('include') && value)
                      .map(([key]) => key.replace('include', '').toLowerCase())
                      .join(', ') || 'No data selected'}</p>
                    <p>Encrypted: {deletionRequest.dataExport?.encryptExport ? 'Yes' : 'No'}</p>
                  </div>
                </div>
              </div>

              <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                <div className="flex items-start space-x-3">
                  <ExclamationTriangleIcon className="h-6 w-6 text-red-500 mt-0.5 flex-shrink-0" />
                  <div className="text-sm text-red-700">
                    <p className="font-medium mb-2">This action cannot be undone after the grace period expires</p>
                    <p>
                      By proceeding, you confirm that you have the authority to delete this tenant account 
                      and understand that all data will be permanently removed.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Type "DELETE {tenant.name}" to confirm
                </label>
                <input
                  type="text"
                  value={deletionRequest.confirmationText}
                  onChange={(e) => updateDeletionRequest({ confirmationText: e.target.value })}
                  className="w-full rounded-md border-gray-300 shadow-sm focus:border-red-300 focus:ring focus:ring-red-200 focus:ring-opacity-50"
                  placeholder={`DELETE ${tenant.name}`}
                />
              </div>

              {showFinalConfirmation && (
                <div className="bg-red-100 border border-red-300 rounded-lg p-4">
                  <div className="text-center">
                    <TrashIcon className="h-12 w-12 text-red-600 mx-auto mb-4" />
                    <h5 className="text-lg font-medium text-red-900 mb-2">
                      Are you absolutely sure?
                    </h5>
                    <p className="text-sm text-red-700 mb-4">
                      This will permanently delete the tenant "{tenant.name}" and all of its data. 
                      This action cannot be undone.
                    </p>
                    <div className="flex space-x-3 justify-center">
                      <button
                        onClick={() => setShowFinalConfirmation(false)}
                        className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSubmit}
                        disabled={isProcessing}
                        className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-red-600 hover:bg-red-700 disabled:opacity-50"
                      >
                        {isProcessing ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2 inline-block"></div>
                            Processing...
                          </>
                        ) : (
                          'Delete Tenant'
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
        <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" onClick={onClose}></div>
        
        <div className="relative transform overflow-hidden rounded-lg bg-white text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-4xl">
          {/* Header */}
          <div className="bg-white px-6 py-4 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <TrashIcon className="h-6 w-6 text-red-600" />
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Delete Tenant Account</h3>
                  <p className="text-sm text-gray-500">{tenant.name}</p>
                </div>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>
          </div>

          {/* Progress Steps */}
          <div className="bg-gray-50 px-6 py-4">
            <div className="flex items-center justify-between">
              {steps.map((step, index) => (
                <div key={step.id} className="flex items-center">
                  <div className={`flex items-center justify-center w-8 h-8 rounded-full border-2 ${
                    currentStep > step.id 
                      ? 'bg-green-500 border-green-500 text-white' 
                      : currentStep === step.id
                      ? 'bg-red-500 border-red-500 text-white'
                      : `bg-white border-gray-300 ${getStepValidation(step.id)}`
                  }`}>
                    {currentStep > step.id ? (
                      <CheckCircleIcon className="h-4 w-4" />
                    ) : (
                      <step.icon className="h-4 w-4" />
                    )}
                  </div>
                  <div className="ml-2 text-sm font-medium text-gray-700 hidden sm:block">
                    {step.name}
                  </div>
                  {index < steps.length - 1 && (
                    <div className="flex-1 mx-4 h-0.5 bg-gray-300 hidden sm:block"></div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Content */}
          <div className="px-6 py-6 max-h-96 overflow-y-auto">
            {renderStepContent()}
          </div>

          {/* Footer */}
          <div className="bg-gray-50 px-6 py-3 flex justify-between">
            <div>
              {currentStep > 1 && !showFinalConfirmation && (
                <button
                  onClick={handlePrevious}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Previous
                </button>
              )}
            </div>
            
            {!showFinalConfirmation && (
              <div className="flex space-x-3">
                <button
                  onClick={onClose}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                
                {currentStep < 4 ? (
                  <button
                    onClick={handleNext}
                    disabled={!validateStep(currentStep)}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    Next
                  </button>
                ) : (
                  <button
                    onClick={() => setShowFinalConfirmation(true)}
                    disabled={!validateStep(4)}
                    className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <TrashIcon className="h-4 w-4 mr-2" />
                    Delete Tenant
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenantDeletionWorkflow;